import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Chip,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';

const drawerWidth = 260;

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
}

interface SidebarProps {
  navigation: NavItem[];
  userRole: string | null;
  onLogout: () => void;
  isMobile?: boolean;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export default function Sidebar({
  navigation,
  userRole,
  onLogout,
  isMobile = false,
  mobileOpen = false,
  onMobileClose = () => {},
}: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavClick = (path: string) => {
    navigate(path);
    if (isMobile) {
      onMobileClose();
    }
  };

  const handleLogoutClick = () => {
    if (isMobile) {
      onMobileClose();
    }
    onLogout();
  };

  const drawerContent = (
    <>
      {/* Logo Header */}
      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mt: isMobile ? '64px' : 0,
        }}
      >
        <Box
          component="img"
          src="/logo192.png"
          alt="SimplyPayroll"
          sx={{
            width: 44,
            height: 44,
          }}
        />
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: '1.1rem',
              color: 'text.primary',
              lineHeight: 1.2,
            }}
          >
            SimplyPayroll
          </Typography>
          <Typography variant="caption" color="text.secondary">
            1099 Tracking
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mx: 2 }} />

      {/* Navigation */}
      <Box sx={{ flex: 1, py: 2, px: 1.5 }}>
        <List disablePadding>
          {navigation.map((item) => {
            const isSelected = location.pathname === item.path;
            const tourId = item.label.toLowerCase().replace(/\s+/g, '-');
            return (
              <ListItemButton
                key={item.label}
                data-tour={`nav-${tourId}`}
                onClick={() => handleNavClick(item.path)}
                selected={isSelected}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  py: 1.25,
                  transition: 'all 0.15s ease',
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.25)',
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  },
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: isSelected ? 'inherit' : 'text.secondary',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: isSelected ? 600 : 500,
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      {/* User Section & Logout */}
      <Box sx={{ p: 2 }}>
        <Divider sx={{ mb: 2 }} />

        {/* Role Badge */}
        {userRole && (
          <Box sx={{ mb: 2, px: 1 }}>
            <Chip
              label={userRole === 'Admin' ? 'Administrator' : 'Client'}
              size="small"
              color={userRole === 'Admin' ? 'secondary' : 'primary'}
              variant="outlined"
              sx={{ fontWeight: 600, width: '100%' }}
            />
          </Box>
        )}

        {/* Logout Button */}
        <ListItemButton
          onClick={handleLogoutClick}
          sx={{
            borderRadius: 2,
            py: 1.25,
            color: 'error.main',
            '&:hover': {
              bgcolor: 'error.main',
              color: 'white',
              '& .MuiListItemIcon-root': {
                color: 'white',
              },
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: 'error.main' }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText
            primary="Sign Out"
            primaryTypographyProps={{
              fontSize: '0.9rem',
              fontWeight: 500,
            }}
          />
        </ListItemButton>
      </Box>
    </>
  );

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? mobileOpen : true}
      onClose={onMobileClose}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile
      }}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          border: 'none',
          bgcolor: 'background.paper',
          boxShadow: isMobile ? '4px 0 16px rgba(0,0,0,0.1)' : '2px 0 8px rgba(0,0,0,0.04)',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}

export { drawerWidth };
